import { Cancel } from "@mui/icons-material";
import {
  Box,
  Button,
  CircularProgress,
  Container,
  Grid2,
  IconButton,
  TextField,
  Typography,
} from "@mui/material";
import { Modal, Modals } from "components";
import { ModalProps } from "..";
import { useEffect, useState } from "react";
import { IUserViewModel } from "viewModels";
import { Controller, useForm } from "react-hook-form";
import { IUpdateCompanyUserInputModel } from "inputModels";
import { EmailController } from "controllers/EmailController";
import { validateEmail } from "utils/validateEmail";
import { usePersistedState } from "hooks";
import { decryptCode } from "utils/crypto";
import { useCompanyUser } from "contexts";
import { CompanyUserController } from "controllers";

interface IData {
  contactValue: string;
  contactLabel: string;
  codeExpected: string;
}

interface EditCompanyUserModalProps extends ModalProps {
  dataCode: IData;
  companyUser: IUserViewModel;
  onCodeChange: (newCode: string) => void;
}

type FormValues = {
  name: string;
  email: string;
  code: string;
  document: string;
};

const EditCompanyUserModal: React.FC<EditCompanyUserModalProps> = ({
  onCodeChange,
  companyUser,
  dataCode,
  ...props
}) => {
  const TIMER = 60000;
  const { setCompanyUsers } = useCompanyUser();
  const [selected, setSelected] = useState<IUserViewModel>(companyUser);
  const [requesting, setRequesting] = useState(false);
  const [editEmail, setEditEmail] = useState(false);
  const [dateToSend, setDateToSend] = usePersistedState<number | undefined>(
    "dateToSend",
    undefined
  );
  const [disableResend, setDisableResend] = useState(
    dateToSend === undefined ? false : dateToSend + TIMER > Date.now()
  );
  const [secondsRemaining, setSecondsRemaining] = useState(TIMER / 1000);

  const defaultValues: FormValues = {
    name: companyUser.name,
    email: companyUser.email,
    code: "",
    document: companyUser.document,
  };

  const {
    control,
    setValue,
    watch,
    clearErrors,
    handleSubmit,
    setError,
    formState: { errors },
  } = useForm<FormValues>({
    defaultValues,
    reValidateMode: "onChange",
    shouldFocusError: false,
  });

  const code = watch("code");
  const formData = watch();

  // envia código de verificação por e-mail
  const handleSendConfirmationEmail = async () => {
    setDateToSend(Date.now());
    EmailController.sendCodeVerification({
      to: formData.email,
      subject: "Edição de e-mail",
      description:
        "Utilize o código abaixo para editar seu email no Legal Control Audit:",
    }).then((res) => {
      onCodeChange(decryptCode(res));
    });
  };

  const onSubmit = async (data: FormValues) => {
    clearErrors();
    setRequesting(true);
    let hasError = false;

    // valida nome
    if (!data.name?.trim()) {
      setError("name", { message: "Informe o nome" });
      hasError = true;
    }

    // valida email
    if (!data.email?.trim()) {
      setError("email", { message: "Informe o email" });
      hasError = true;
    } else if (!validateEmail(data.email)) {
      setError("email", { message: "Email inválido" });
      hasError = true;
    } else if (
      data.email !== selected.email &&
      (await CompanyUserController.verifyAlreadyEmail(data.email)).data
    ) {
      setError("email", { message: "Este email já existe" });
      hasError = true;
    }

    // requer código se email mudou
    const emailChanged = data.email !== selected.email;
    if (emailChanged) {
      if (!data.code) {
        setError("code", { message: "Insira o código de verificação" });
        hasError = true;
      } else if (data.code !== dataCode.codeExpected) {
        setError("code", { message: "Código não corresponde" });
        hasError = true;
      }
    }

    if (hasError) {
      setRequesting(false);
      return;
    }

    // realiza atualização

    const update: IUpdateCompanyUserInputModel = {
      id: companyUser.id,
      name: data.name,
      email: data.email,
    };
    await CompanyUserController.updateUser(update)
      .then(() => {
        setCompanyUsers((prev) => {
          const idx = prev.findIndex((u) => u.id === companyUser.id);
          prev[idx] = { ...prev[idx], name: data.name, email: data.email };
          return [...prev];
        });
        props.onClose?.();
      })
      .catch((error) => {
        if (error.response?.status === 400) {
          setError("code", { message: "Código inválido" });
        } else {
          setError("code", { message: "Erro ao atualizar usuário" });
        }
      })
      .finally(() => {
        setRequesting(false);
        setValue("code", "");
      });
  };

  // contador de reenvio
  useEffect(() => {
    let interval: NodeJS.Timeout;
    if (dateToSend !== undefined) {
      interval = setInterval(() => {
        const disabled = dateToSend + TIMER > Date.now();
        setDisableResend(disabled);
        setSecondsRemaining((prev) => prev - 1);
        if (!disabled) {
          setSecondsRemaining(TIMER / 1000);
          setDateToSend(undefined);
          clearInterval(interval);
        }
      }, 1000);
    }
    return () => clearInterval(interval);
  }, [dateToSend, setDateToSend]);

  // atualiza valores quando companyUser muda
  useEffect(() => {
    setValue("name", companyUser.name);
    setValue("email", companyUser.email);
    setValue("document", companyUser.document);
    setSelected(companyUser);
  }, [companyUser, setValue]);

  return (
    <Modal
      {...props}
      sx={{
        display: "flex",
        flexDirection: "column",
        width: "23rem",
        height: "34rem",
        p: 1,
      }}
    >
      <IconButton
        size="small"
        onClick={props.onClose}
        color="error"
        sx={{ position: "absolute", top: 5, right: 5 }}
      >
        <Cancel />
      </IconButton>

      <Box sx={{ flex: 1, overflowY: "auto", mt: 4 }}>
        <Container>
          <Typography variant="h5" color="primary" mt={1} mb={2}>
            Editar Empresa
          </Typography>

          <Grid2 mb={2} display="flex" flexDirection="column" gap={2}>
            <Controller
              name="document"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Documento"
                  disabled
                />
              )}
            />

            <Controller
              name="name"
              control={control}
              render={({ field }) => (
                <TextField
                  {...field}
                  fullWidth
                  size="small"
                  label="Nome"
                  error={!!errors.name}
                  helperText={errors.name?.message}
                  onChange={(e) => {
                    field.onChange(e.target.value);
                    clearErrors("name");
                  }}
                />
              )}
            />
          </Grid2>

          <Button
            fullWidth
            variant="contained"
            onClick={() => setEditEmail((v) => !v)}
            disabled={requesting}
          >
            {editEmail ? "Cancelar Edição" : "Editar Email"}
          </Button>

          {editEmail && (
            <>
              <Grid2 mb={2} mt={2}>
                <Controller
                  name="email"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="E-mail"
                      error={!!errors.email}
                      helperText={errors.email?.message}
                      onChange={(e) => {
                        field.onChange(e.target.value);
                        clearErrors("email");
                      }}
                    />
                  )}
                />
              </Grid2>

              <Grid2 display="flex" justifyContent="center" mb={2}>
                <Button
                  variant="contained"
                  onClick={handleSendConfirmationEmail}
                  disabled={disableResend}
                  sx={{ width: "12rem" }}
                >
                  {disableResend
                    ? `Reenviar (${secondsRemaining})`
                    : "Enviar código"}
                </Button>
              </Grid2>

              <Grid2 mb={2}>
                <Controller
                  name="code"
                  control={control}
                  render={({ field }) => (
                    <TextField
                      {...field}
                      fullWidth
                      size="small"
                      label="Código de Verificação"
                      disabled={!editEmail}
                      error={!!errors.code}
                      helperText={errors.code?.message}
                    />
                  )}
                />
              </Grid2>
            </>
          )}
        </Container>
      </Box>

      <Box
        component="footer"
        sx={{
          p: 2,
          backgroundColor: "background.paper",
        }}
      >
        <Grid2 display="flex" justifyContent="space-between" mt={4}>
          <Button
            variant="contained"
            onClick={() => props.onClose?.()}
            disabled={requesting}
          >
            Cancelar
          </Button>

          <Button
            variant="contained"
            onClick={handleSubmit(onSubmit)}
            disabled={requesting}
          >
            Salvar
            {requesting && <CircularProgress size={15} sx={{ ml: 1 }} />}
          </Button>
        </Grid2>
      </Box>

      <Modals.RecoverPasswordModal
        open={false}
        typeModal="recoverPassword"
        onCodeChange={() => {}}
        data={{ codeExpected: "", contactLabel: "", contactValue: "" }}
        onClose={() => {}}
      />
    </Modal>
  );
};

export default EditCompanyUserModal;
